import { MultiSelectComboboxField } from '@/components/form-components/MultiSelectCombobox'
import { SingleSelectComboboxField } from '@/components/form-components/SingleSelectCombobox'
import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { RestSchemas, useRestClient } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { useCurrentOrganisation } from '../useCurrentOrganisation'
import { Button } from '@/components/shadcn/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'

type ExistingAiGateway = RestSchemas['Messages.AiGateway.ExistingAiGateway']

const formSchema = z.object({
  name: z.string().min(1, 'Name is required').max(120),
  providerId: z.number().min(1, 'Provider is required'),
  apiKey: z.string().optional().default(''),
  allowedModels: z.array(z.string()).default([]),
})

export type TAiGatewayForm = z.infer<typeof formSchema>

type AiGatewayFormProps = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  selectedGateway?: ExistingAiGateway
}

export const AiGatewayForm = (props: AiGatewayFormProps) => {
  const [sheetOpen, setSheetOpen] = useState(false)
  const edit = !!props.selectedGateway
  const { id: orgId } = useCurrentOrganisation()
  const {
    getAiGatewayApiListGateways,
    putAiGatewayApiAddGateway,
    postAiGatewayApiUpdateGateway,
    getProviderRegistrationApiListProviders,
  } = useRestClient()

  const { data: providers } = getProviderRegistrationApiListProviders.useQuery(undefined)

  const invalidate = () =>
    getAiGatewayApiListGateways.invalidateQueries({
      parameters: { query: { orgId } },
    })

  const { mutate: addGateway, isPending: isAdding } = putAiGatewayApiAddGateway.useMutation(undefined, {
    onSuccess: () => {
      invalidate()
      setSheetOpen(false)
      toast.success('AI Gateway created')
      props.onSubmit?.()
    },
    onError: () => toast.error('Failed to create AI Gateway'),
  })

  const { mutate: updateGateway, isPending: isUpdating } = postAiGatewayApiUpdateGateway.useMutation(undefined, {
    onSuccess: () => {
      invalidate()
      setSheetOpen(false)
      toast.success('AI Gateway updated')
      props.onSubmit?.()
    },
    onError: () => toast.error('Failed to update AI Gateway'),
  })

  const submitHandler = useCallback(
    (data: TAiGatewayForm) => {
      if (edit && props.selectedGateway) {
        if (props.selectedGateway.id == null) {
          toast.error('Unable to update AI Gateway: missing gateway ID')
          return
        }
        updateGateway({
          body: {
            id: props.selectedGateway.id,
            name: data.name,
            providerId: data.providerId,
            apiKey: data.apiKey?.trim() ? data.apiKey : null,
            allowedModels: data.allowedModels,
          },
        })
      } else {
        if (!data.apiKey?.trim()) {
          toast.error('API key is required')
          return
        }
        addGateway({
          body: {
            name: data.name,
            orgId,
            providerId: data.providerId,
            apiKey: data.apiKey,
            allowedModels: data.allowedModels,
          },
        })
      }
    },
    [edit, props.selectedGateway, addGateway, updateGateway, orgId]
  )

  if (isAdding || isUpdating) return <FormComponentLoading />

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
      wide
    >
      <FormFields
        selectedGateway={props.selectedGateway}
        providers={providers ?? []}
        edit={edit}
        submitHandler={submitHandler}
      />
    </SheetWithFormComponent>
  ) : (
    <FormFields
      selectedGateway={props.selectedGateway}
      providers={providers ?? []}
      edit={edit}
      submitHandler={submitHandler}
    />
  )
}

function FormFields({
  selectedGateway,
  providers,
  edit,
  submitHandler,
}: {
  selectedGateway?: ExistingAiGateway
  providers: RestSchemas['Messages.Agent.ExistingRegisteredProvider'][]
  edit: boolean
  submitHandler: (data: TAiGatewayForm) => void
}) {
  const form = useForm<TAiGatewayForm>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: selectedGateway?.name ?? '',
      providerId: selectedGateway?.providerId ?? 0,
      apiKey: '',
      allowedModels: selectedGateway?.allowedModels ?? [],
    },
  })

  const providerId = form.watch('providerId')

  const providerOptions = useMemo(
    () =>
      providers.map((p) => ({
        value: p.id,
        label: p.name,
        description: p.key,
      })),
    [providers]
  )

  const modelOptions = useMemo(() => {
    const provider = providers.find((p) => p.id === providerId)
    return (
      provider?.models?.map((m) => ({
        value: m.model,
        label: m.name || m.model,
      })) ?? []
    )
  }, [providers, providerId])

  // Drop allowed models that are not on the newly selected provider
  const allowedModels = form.watch('allowedModels')
  useEffect(() => {
    if (!modelOptions.length || !allowedModels?.length) return
    const allowed = new Set(modelOptions.map((o) => o.value))
    const filtered = allowedModels.filter((m) => allowed.has(m))
    if (filtered.length !== allowedModels.length) {
      form.setValue('allowedModels', filtered)
    }
  }, [modelOptions, allowedModels, form])

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(submitHandler)} className="flex flex-col gap-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Org GenAI gateway" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <SingleSelectComboboxField
          control={form.control}
          name="providerId"
          label="Provider"
          options={providerOptions}
          placeholder="Select provider"
        />
        <FormField
          control={form.control}
          name="apiKey"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{edit ? 'API key (leave blank to keep)' : 'API key'}</FormLabel>
              <FormControl>
                <Input {...field} type="password" autoComplete="off" placeholder={edit ? '••••••••' : ''} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <MultiSelectComboboxField
          control={form.control}
          name="allowedModels"
          label="Allowed models"
          options={modelOptions}
          placeholder="Select models from the provider catalog"
        />
        <Button type="submit" className="self-end">
          {edit ? 'Save' : 'Create'}
        </Button>
      </form>
    </Form>
  )
}
